'use client';

import { useMemo } from 'react';
import type { NavItem } from '@/types';
import type { AuthUser } from '@/features/auth';
import { useAuth } from '@/contexts/auth-context';
import { isSuperAdmin, hasAnyPermission } from '@/features/auth';
import { navItems } from '@/config/nav-config';
import { useAppSettings } from '@/features/configurations';

/** Reachable by any authenticated account, whatever its permissions are. */
export const NO_ACCESS_FALLBACK_ROUTE = '/dashboard/settings/account';

function isItemVisible(
  item: NavItem,
  user: AuthUser,
  settings: Record<string, unknown>
): boolean {
  // Feature flag check
  if (item.featureFlag && !settings[item.featureFlag]) return false;

  if (!item.access?.requiredPermissions?.length) return true;
  if (isSuperAdmin(user)) return true;
  return hasAnyPermission(user, item.access.requiredPermissions);
}

function filterItems(
  items: NavItem[],
  user: AuthUser,
  settings: Record<string, unknown>
): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    if (!isItemVisible(item, user, settings)) return acc;
    const filteredChildren = item.items
      ? filterItems(item.items, user, settings)
      : undefined;
    acc.push({ ...item, items: filteredChildren });
    return acc;
  }, []);
}

export function useFilteredNavItems(items: NavItem[]): NavItem[] {
  const { user } = useAuth();
  const settings = useAppSettings();

  return useMemo(
    () => (user ? filterItems(items, user, settings) : []),
    [items, user, settings]
  );
}

/**
 * Returns the URL of the first nav item the current user has access to.
 *
 * The live settings have to come in: passing `{}` treated every feature-flagged
 * item as hidden, so a user whose only screen sits behind a flag was bounced to
 * `/dashboard` — a page they have no permission to read.
 *
 * The no-match fallback is account settings, not `/dashboard`: an account with
 * no module permissions has none for the dashboard either, and sending it there
 * only trades one "Access Denied" for a page of zeros (OMA-605).
 */
export function useFirstAccessibleRoute(): string {
  const { user } = useAuth();
  const settings = useAppSettings();

  return useMemo(() => {
    if (!user) return '/dashboard';
    if (isSuperAdmin(user)) return '/dashboard';

    const firstItem = navItems.find((item) =>
      isItemVisible(item, user, settings)
    );
    return firstItem?.url ?? NO_ACCESS_FALLBACK_ROUTE;
  }, [user, settings]);
}
